import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EtlJobName, EtlJobRequest, etlJobsApi } from '../services/etlJobsApi';
import { fmtLisbonTime } from '../utils/replenishment';

export const POLL_INTERVAL_MS = 10_000;
export const STALE_QUEUE_MS = 3 * 60_000;

const isActive = (r: EtlJobRequest | null): boolean => r?.status === 'queued' || r?.status === 'running';

interface Props {
  job: EtlJobName;
  /** Chamado quando um pedido que estava a decorrer termina com sucesso (recarregar os dados). */
  onFinished: () => void;
}

/**
 * Botão "Gerar agora": pede ao backend que o ETL da lista corra já. Mostra o estado
 * (na fila -> a gerar -> pronta/erro) e consulta de 10 em 10 s enquanto está activo.
 */
const GenerateNowButton: React.FC<Props> = ({ job, onFinished }) => {
  const [request, setRequest] = useState<EtlJobRequest | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const wasActive = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const apply = useCallback((next: EtlJobRequest | null) => {
    const active = isActive(next);
    if (wasActive.current && next?.status === 'success') onFinishedRef.current();
    wasActive.current = active;
    setRequest(next);
  }, []);

  useEffect(() => {
    let alive = true;
    etlJobsApi.latest(job)
      .then((s) => { if (alive) { setEnabled(s.enabled); apply(s.request); } })
      .catch(() => undefined);  // sem estado: o botão continua utilizável
    return () => { alive = false; };
  }, [job, apply]);

  const active = isActive(request);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setNow(Date.now());
      etlJobsApi.latest(job).then((s) => apply(s.request)).catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, job, apply]);

  const click = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await etlJobsApi.request(job);
      setNow(Date.now());
      apply(created);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível pedir a geração.');
    } finally {
      setSubmitting(false);
    }
  };

  const staleQueue = request?.status === 'queued'
    && now - new Date(request.requested_at).getTime() > STALE_QUEUE_MS;

  let statusText: string | null = null;
  if (request?.status === 'queued') statusText = 'Na fila…';
  else if (request?.status === 'running') statusText = 'A gerar…';
  else if (request?.status === 'success') statusText = `Pronta às ${fmtLisbonTime(request.finished_at)}`;
  else if (request?.status === 'error') statusText = `Erro: ${request.message || 'falhou a geração'}`;

  return (
    <div className="d-inline-flex flex-column align-items-end">
      <div className="d-flex align-items-center gap-2">
        {statusText && (
          <span role="status"
                className={`small ${request?.status === 'error' ? 'text-danger' : 'text-muted'}`}>{statusText}</span>
        )}
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={click}
                disabled={submitting || active || !enabled}
                title={enabled ? 'Corre o cálculo já (demora alguns minutos)' : 'Geração a pedido desligada neste ambiente'}>
          {active && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
          ⟳ Gerar agora
        </button>
      </div>
      {staleQueue && (
        <div className="small text-warning">Sem ETL a processar pedidos neste ambiente.</div>
      )}
      {error && <div className="small text-danger">{error}</div>}
    </div>
  );
};

export default GenerateNowButton;
